
// https://developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection/getStats#browser_compatibility

export interface ITrackStatsInfo {
  type: 'video'|'audio',
  track: any,
  inboundRtp: any,
  codec: any,
}

function _getStats (stats: RTCStatsReport): ITrackStatsInfo[] {
  const inboundRtps: any[] = []

  stats.forEach(x => {
    if (x.type === 'inbound-rtp') {
      inboundRtps.push(x)
    }
  })

  return inboundRtps.map(inbound => {
    const codec = stats.get(inbound.codecId)
    const track = stats.get(inbound.trackId)

    return {
      type: inbound.mediaType,
      inboundRtp: inbound,
      codec,
      track
    }
  })
}

async function getStats (pc: RTCPeerConnection | null): Promise<ITrackStatsInfo[]> {
  if (!pc) return []
  return new Promise<ITrackStatsInfo[]>((resolve) => {
    pc
      .getStats(null)
      ?.then(report => {
        resolve(_getStats(report))
      })
  }).catch(() => [] as ITrackStatsInfo[])
}

/**
 *
 * @typedef {{
  * video?:{
  *    codec: string,
  *    payloadType: string,
  *    sdpFmtpLine: string,
  *    bytesReceived: number,
  *    firCount: number,
  *    pliCount: number,
  *    frameHeight: number,
  *    frameWidth: number,
  *    framesDecoded: number,
  *    framesDropped: number,
  *    framesPerSecond: number,
  *    framesReceived: number,
  *    jitter: number,
  *    jitterBufferDelay: number,
  *    keyFramesDecoded: number,
  *    nackCount: number,
  *    packetsLost: number,
  *    packetsReceived: number,
  * },
 *  audio?:{
 *    codec: string,
 *    payloadType: string,
 *    sdpFmtpLine: string,
 *    audioLevel: number,
 *    bytesReceived: number,
 *    jitter: number,
 *    jitterBufferDelay: number,
 *    packetsLost: number,
 *    packetsReceived: number,
 *    totalSamplesDuration: number,
 *    totalSamplesReceived: number,
 *  }
 * }} StatsSnapshoot
 *
 * @return {StatsSnapshoot}
 */
function getStatsSnapshoot (stats: ITrackStatsInfo[]) {
  const v = stats?.filter(x => x.type === 'video')[0]
  const a = stats?.filter(x => x.type === 'audio')[0]

  return {
    video: !v
      ? undefined
      : {
        codec: v.codec?.mimeType,
        payloadType: v.codec?.payloadType,
        sdpFmtpLine: v.codec?.sdpFmtpLine,
        bytesReceived: v.inboundRtp?.bytesReceived,
        firCount: v.inboundRtp?.firCount,
        pliCount: v.inboundRtp?.pliCount,
        frameHeight: v.inboundRtp?.frameHeight,
        frameWidth: v.inboundRtp?.frameWidth,
        framesDecoded: v.inboundRtp?.framesDecoded,
        framesDropped: v.inboundRtp?.framesDropped,
        framesPerSecond: v.inboundRtp?.framesPerSecond,
        framesReceived: v.inboundRtp?.framesReceived,
        jitter: v.inboundRtp?.jitter,
        jitterBufferDelay: v.inboundRtp?.jitterBufferDelay,
        keyFramesDecoded: v.inboundRtp?.keyFramesDecoded,
        nackCount: v.inboundRtp?.nackCount,
        packetsLost: v.inboundRtp?.packetsLost,
        packetsReceived: v.inboundRtp?.packetsReceived
      },
    audio: !a
      ? undefined
      : {
        codec: a.codec?.mimeType,
        payloadType: a.codec?.payloadType,
        sdpFmtpLine: a.codec?.sdpFmtpLine,
        bytesReceived: a.inboundRtp?.bytesReceived,
        audioLevel: a.inboundRtp?.audioLevel,
        jitter: a.inboundRtp?.jitter,
        jitterBufferDelay: a.inboundRtp?.jitterBufferDelay,
        packetsLost: a.inboundRtp?.packetsLost,
        packetsReceived: a.inboundRtp?.packetsReceived,
        totalSamplesDuration: a.inboundRtp?.totalSamplesDuration,
        totalSamplesReceived: a.inboundRtp?.totalSamplesReceived
      }
  }
}

export {
  getStats,
  getStatsSnapshoot
}
